var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PacemConeElement_1;
import { CustomElement, P, Watch, PropertyConverters } from '@pacem/pacem-core';
import { computeSharpVertexNormals } from '../types';
import { MeshGeometry } from '../geometry';
import { Pacem3DPrimitiveElement } from './primitive';
import { TAG_MIDDLE_NAME, DEG2RAD } from '../constants';
//namespace Pacem.Components.Drawing3D {
let PacemConeElement = PacemConeElement_1 = class PacemConeElement extends Pacem3DPrimitiveElement {
    static createMeshGeometry(radius = 1, height = 1, sides = 18, heightSegments = 5, capSegments = 1) {
        const r = radius;
        const nodes = [], uv = [], indices = [];
        const theta = DEG2RAD * (360.0 / sides);
        const slope = r / height;
        const fY = height / heightSegments;
        const fU = 1.0 / sides;
        // top vertex
        nodes.push({ x: .0, y: height, z: .0 });
        // ======================================
        // side loops
        for (let j = 1; j <= heightSegments; j++) {
            const y = height - fY * j;
            const v = y / height;
            const rj = (height - y) * slope;
            // for loops other than tip loop
            const y_prev = y + fY;
            const v_prev = y_prev / height;
            for (let i = 0; i < sides; i++) {
                const angle = theta * i;
                const x = Math.cos(angle) * rj, z = -Math.sin(angle) * rj;
                nodes.push({ x, y, z });
                const index = 1 + sides * (j - 1) + i;
                const index_prev = i == 0 ? index + sides - 1 : index - 1;
                const u = i * fU;
                const u_prev = i == 0 ? (1 - fU) : fU * (i - 1);
                if (j == 1) {
                    // tip loop
                    // 1 triangle
                    indices.push(0, index_prev, index);
                    uv.push({ x: u, y: 1 }, { x: u_prev, y: v }, { x: u, y: v });
                    continue;
                }
                const prevLoopIndex = 1 + sides * (j - 2) + i;
                const prevLoopIndex_prev = i == 0 ? prevLoopIndex + sides - 1 : prevLoopIndex - 1;
                // quad
                const A = prevLoopIndex_prev;
                const B = index_prev;
                const C = index;
                const D = prevLoopIndex;
                indices.push(C, A, B);
                indices.push(C, D, A);
                const uvA = { x: u_prev, y: v_prev };
                const uvB = { x: u_prev, y: v };
                const uvC = { x: u, y: v };
                const uvD = { x: u, y: v_prev };
                uv.push(uvC, uvA, uvB);
                uv.push(uvC, uvD, uvA);
            }
        }
        // bottom cap
        const startCapIndex = 1 + (heightSegments - 1) * sides;
        // bottom vertex (pivot)
        nodes.push({ x: .0, y: 0, z: .0 });
        const bottomIndex = startCapIndex + sides;
        // ======================================
        for (let j = 1; j <= capSegments; j++) {
            const rj_norm = j / capSegments;
            const rj = radius * rj_norm;
            // for loops other than bottom vertex loop
            const rj_norm_prev = (j - 1) / capSegments;
            for (let i = 0; i < sides; i++) {
                const angle = theta * i, cos = Math.cos(angle), sin = Math.sin(angle);
                const angle_prev = angle - theta, cos_prev = Math.cos(angle_prev), sin_prev = Math.sin(angle_prev);
                const u = .5 * (1 + cos * rj_norm), v = .5 * (1 + sin * rj_norm);
                const u_prev = .5 * (1 + cos_prev * rj_norm), v_prev = .5 * (1 + sin_prev * rj_norm);
                let index, index_prev;
                if (j != capSegments) {
                    const x = Math.cos(angle) * rj, z = -Math.sin(angle) * rj;
                    nodes.push({ x, y: 0, z });
                    index = bottomIndex + 1 + (j - 1) * sides + i;
                    index_prev = i == 0 ? index + sides - 1 : index - 1;
                }
                else {
                    index = startCapIndex + i;
                    index_prev = i == 0 ? startCapIndex + sides - 1 : index - 1;
                }
                if (j == 1) {
                    // bottom vertex loop
                    // 1 triangle
                    indices.push(bottomIndex, index, index_prev);
                    uv.push({ x: .5, y: .5 }, { x: u, y: v }, { x: u_prev, y: v_prev });
                    continue;
                }
                const prevLoopU = .5 * (1 + cos * rj_norm_prev), prevLoopV = .5 * (1 + sin * rj_norm_prev);
                const prevLoopU_prev = .5 * (1 + cos_prev * rj_norm_prev), prevLoopV_prev = .5 * (1 + sin_prev * rj_norm_prev);
                const prevLoopIndex = bottomIndex + 1 + (j - 2) * sides + i;
                const prevLoopIndex_prev = i == 0 ? prevLoopIndex + sides - 1 : prevLoopIndex - 1;
                // quad
                const A = prevLoopIndex_prev;
                const B = index_prev;
                const C = index;
                const D = prevLoopIndex;
                indices.push(A, D, B);
                indices.push(B, D, C);
                const uvA = { x: prevLoopU_prev, y: prevLoopV_prev };
                const uvB = { x: u_prev, y: v_prev };
                const uvC = { x: u, y: v };
                const uvD = { x: prevLoopU, y: prevLoopV };
                uv.push(uvA, uvD, uvB);
                uv.push(uvB, uvD, uvC);
            }
        }
        // put the whole thing together
        const geom = new MeshGeometry(nodes, indices, uv);
        geom.key = `cone_${radius}x${height}_s${sides}_h${heightSegments}_c${capSegments}`;
        computeSharpVertexNormals(geom);
        // TODO: barycenter, boundingBox, boundingSphere
        return geom;
    }
    createDefaultGeometry() {
        return PacemConeElement_1.createMeshGeometry();
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'radius':
            case 'height':
            case 'sides':
            case 'heightSegments':
            case 'capSegments':
                this.geometry = PacemConeElement_1.createMeshGeometry(this.radius, this.height, this.sides, this.heightSegments, this.capSegments);
                break;
        }
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemConeElement.prototype, "radius", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemConeElement.prototype, "height", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemConeElement.prototype, "sides", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemConeElement.prototype, "heightSegments", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemConeElement.prototype, "capSegments", void 0);
PacemConeElement = PacemConeElement_1 = __decorate([
    CustomElement({ tagName: P + '-' + TAG_MIDDLE_NAME + '-primitive-cone' })
], PacemConeElement);
export { PacemConeElement };
